import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import searchItems from 'utils/searchItems';

import { getDebouncedSearchValue } from 'store/search/selectors';

import { getCompany } from './company.selectors';

const selectLoanOfficersManagement = createSelector(
  getCompany,
  company => company.loanOfficersManagement,
);

const selectRelationsBetweenLOAndLOM = createSelector(
  selectLoanOfficersManagement,
  ({ relations }) => relations,
);

const selectLoanOfficerUsers = createSelector(
  selectLoanOfficersManagement,
  ({ loanOfficerUsers }) => loanOfficerUsers,
);

const selectLoanOfficerUsersIds = createSelector(
  selectLoanOfficersManagement,
  ({ loanOfficerUsersIds }) => loanOfficerUsersIds,
);

const selectLoanOfficerManagerUsers = createSelector(
  selectLoanOfficersManagement,
  ({ loanOfficerManagerUsers }) => loanOfficerManagerUsers,
);

const selectLoanOfficerManagerUsersIds = createSelector(
  selectLoanOfficersManagement,
  ({ loanOfficerManagerUsersIds }) => loanOfficerManagerUsersIds,
);

const selectLoanOfficerUsersArray = createSelector(
  [selectLoanOfficerUsers, selectLoanOfficerUsersIds, selectRelationsBetweenLOAndLOM],
  (loanOfficerUsers, loanOfficerUsersIds, relations) =>
    loanOfficerUsersIds.reduce((acc, loanOfficerUserId) => {
      const loanOfficerUser = loanOfficerUsers[loanOfficerUserId];
      const loanOfficerRelations = relations[loanOfficerUserId];

      if (loanOfficerUser) {
        const extendedLoanOfficerUser = { ...loanOfficerUser, loanOfficerManagerId: null };

        if (loanOfficerRelations) {
          extendedLoanOfficerUser.loanOfficerManagerId = loanOfficerRelations.ownerId;
        }

        acc.push(extendedLoanOfficerUser);
      }

      return acc;
    }, []),
);

const selectLoanOfficerManagerUsersArray = createSelector(
  [selectLoanOfficerManagerUsers, selectLoanOfficerManagerUsersIds],
  (loanOfficerManagerUsers, loanOfficerManagerUsersIds) =>
    loanOfficerManagerUsersIds.reduce((acc, loanOfficerUserId) => {
      const loanOfficerManagerUser = loanOfficerManagerUsers[loanOfficerUserId];

      if (loanOfficerManagerUser) {
        acc.push(loanOfficerManagerUser);
      }

      return acc;
    }, []),
);

export const selectLoanOfficersManagementCells = memoizeOne(() => [
  {
    id: 'email',
    align: 'left',
    disablePadding: false,
    label: 'Email',
    sort: true,
    show: true,
    style: { width: '30%' },
  },
  {
    id: 'firstName',
    align: 'left',
    disablePadding: false,
    label: 'First name',
    sort: true,
    show: true,
    style: { width: '20%' },
  },
  {
    id: 'lastName',
    align: 'left',
    disablePadding: false,
    label: 'Last name',
    sort: true,
    show: true,
    style: { width: '20%' },
  },
  {
    id: 'loanOfficerManager',
    align: 'left',
    disablePadding: false,
    label: 'Loan Officer Manager',
    sort: false,
    show: true,
    style: { width: '30%', overflow: 'visible' },
  },
]);

export const selectFilteredLoanOfficerUsers = createSelector(
  getDebouncedSearchValue,
  selectLoanOfficerUsersArray,
  searchItems(({ firstName, lastName, email }) => [firstName, lastName, email]),
);

export const selectLoanOfficerManagerOptions = createSelector(
  selectLoanOfficerManagerUsersArray,
  loanOfficerManagers =>
    loanOfficerManagers?.map(({ id, firstName, lastName }) => ({
      name: `${firstName} ${lastName}`,
      id,
    })),
);
